import { z } from 'zod';

import {
  PACKAGE_DEPLOYMENT_STATUSES,
  PACKAGE_DEPLOYMENT_UPGRADE_MODES,
  PACKAGE_MATURITY_STAGES,
  PACKAGE_TYPES,
} from '../constants/enums';

const FORBIDDEN_CONFIG_KEYS = new Set([
  'secret',
  'credential',
  'token',
  'api_key',
  'password',
  'private_key',
  'approval_history',
  'raw_trace',
  'raw_prompt',
  'raw_output',
]);

export interface PackageDeploymentConfigError {
  readonly path: string;
  readonly message: string;
}

export type PackageDeploymentConfigValidationResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly errors: readonly PackageDeploymentConfigError[] };

export const packageDeploymentConfigSchema = z
  .record(z.string(), z.unknown())
  .default({})
  .superRefine((value, ctx) => {
    const result = validatePackageDeploymentConfig(value);
    if (result.ok) return;
    for (const error of result.errors) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [error.path], message: error.message });
    }
  });

export const packageDeploymentDependencySchema = z.object({
  package_id: z.string().min(1),
  package_version_id: z.string().min(1),
  content_hash: z.string().min(1),
});

export const packageDeploymentLockSchema = z.object({
  package_id: z.string().min(1),
  package_version_id: z.string().min(1),
  content_hash: z.string().min(1),
  dependencies: z.array(packageDeploymentDependencySchema).default([]),
  tool_requirements: z.array(z.string().min(1)).default([]),
  contract_refs: z.array(z.string().min(1)).default([]),
  package_link_closure_hash: z.string().nullable().optional(),
  compiler_version: z.string().min(1).default('v3.package-compiler.1'),
  generated_at: z.string().nullable().optional(),
});

export const packageDeploymentInstallRequestSchema = z.object({
  context_id: z.string().min(1),
  package_id: z.string().min(1),
  package_version_id: z.string().min(1),
  content_hash: z.string().min(1),
  config: packageDeploymentConfigSchema,
  resolved_package_lock: packageDeploymentLockSchema.optional(),
  deployment_status: z.enum(PACKAGE_DEPLOYMENT_STATUSES).default('active'),
  upgrade_mode: z.enum(PACKAGE_DEPLOYMENT_UPGRADE_MODES).default('manual_review'),
  approval_policy_ref: z.string().nullable().optional(),
  approved_by_actor_id: z.string().nullable().optional(),
  authorized_via_type: z.string().nullable().optional(),
  authorized_via_id: z.string().nullable().optional(),
  authorized_via_membership_id: z.string().nullable().optional(),
  source_action_card_id: z.string().nullable().optional(),
  operating_context_id: z.string().nullable().optional(),
});

export const packageDeploymentStatusPatchSchema = z.object({
  deployment_status: z.enum(PACKAGE_DEPLOYMENT_STATUSES),
});

export const packageDeploymentLoadResultSchema = z.object({
  deployment_id: z.string().min(1),
  binding_id: z.string().min(1),
  context_id: z.string().min(1),
  package: z.object({
    id: z.string().min(1),
    package_type: z.enum(PACKAGE_TYPES),
    version: z.number().int(),
    maturity_stage: z.enum(PACKAGE_MATURITY_STAGES),
    content_hash: z.string().min(1),
  }),
  resolved_package_lock: packageDeploymentLockSchema,
  resolved_package_lock_hash: z.string().min(1),
  config: packageDeploymentConfigSchema,
  config_fingerprint: z.string().min(1),
  lineage: z.array(z.object({ kind: z.string().min(1), id: z.string().min(1) })),
});

export type PackageDeploymentInstallRequest = z.infer<
  typeof packageDeploymentInstallRequestSchema
>;
export type PackageDeploymentLock = z.infer<typeof packageDeploymentLockSchema>;
export type PackageDeploymentLoadResult = z.infer<typeof packageDeploymentLoadResultSchema>;

export function validatePackageDeploymentConfig(
  value: unknown,
): PackageDeploymentConfigValidationResult {
  const errors: PackageDeploymentConfigError[] = [];
  collectForbiddenConfigKeys(value, [], errors);
  return errors.length === 0 ? { ok: true } : { ok: false, errors };
}

export function packageDeploymentConfigFingerprint(config: unknown): string {
  return `cfg_${hashString(stableStringify(config))}`;
}

export function packageDeploymentLockHash(lock: unknown): string {
  return `lock_${hashString(stableStringify(lock))}`;
}

function collectForbiddenConfigKeys(
  value: unknown,
  path: readonly string[],
  errors: PackageDeploymentConfigError[],
): void {
  if (Array.isArray(value)) {
    value.forEach((item, index) => collectForbiddenConfigKeys(item, [...path, String(index)], errors));
    return;
  }
  if (value === null || typeof value !== 'object') return;
  for (const [key, nested] of Object.entries(value)) {
    const nextPath = [...path, key];
    if (FORBIDDEN_CONFIG_KEYS.has(key.toLowerCase())) {
      errors.push({ path: nextPath.join('.'), message: `Forbidden deployment config key: ${key}` });
    }
    collectForbiddenConfigKeys(nested, nextPath, errors);
  }
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  const entries = Object.entries(value).sort(([left], [right]) => left.localeCompare(right));
  return `{${entries
    .map(([key, nested]) => `${JSON.stringify(key)}:${stableStringify(nested)}`)
    .join(',')}}`;
}

function hashString(value: string): string {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}
